import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import {
  Client,
  DownloadLoadProfileDataIn,
  EnergyPerPhaseDataOut,
  LoadProfileCommandEvent,
  PageInfo,
  PermissionsEnum,
} from '../../../api/api-reference';
import { AuthService } from '../../../services/auth.service';
import { generateFileNameAppendix } from '../../../helper/download-filename';
import { UnitConverter } from '../../../pipes/unit-conversion';
import { FileService } from '../../../services/file.service';
import { TableScrollService } from '../../../services/table-scroll.service';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { ReadsType } from '../../scheduled-data-read/scheduled.module';

@Component({
  selector: 'app-energy-per-phase-report-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    AngularSvgIconModule,
    TranslateModule,
    UnitConverter,
    PaginationComponent,
  ],
  templateUrl: './energy-per-phase-details.component.html',
  styleUrl: './energy-per-phase-details.component.scss',
})
export class EnergyPerPhaseDetailsComponent implements AfterViewInit {
  ReadsType = ReadsType;
  PermissionsEnum = PermissionsEnum;
  readOnMeterTime: Date | undefined;
  commandId?: string;
  eventId?: string;
  data: EnergyPerPhaseDataOut[] = [];
  isSelectOptionOpen = false;
  selectedReadOption: ReadsType = ReadsType.Processed;

  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public numberOfDevices: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;

  requestData = new LoadProfileCommandEvent({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    meterId: '',
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private tableScrollService: TableScrollService,
    private fileService: FileService,
    public authService: AuthService,
  ) {
    this.route.queryParams.subscribe((params) => {
      this.requestData.meterId = params['meterId'];
    });

    this.loadTable(1);
  }

  @ViewChild('tableContainer') tableContainer!: ElementRef;

  ngAfterViewInit(): void {
    this.initializeTableEvents();
  }

  private initializeTableEvents(): void {
    const tableContainer = this.tableContainer.nativeElement;
    // Drag events
    tableContainer.addEventListener('mousedown', (event: MouseEvent) =>
      this.tableScrollService.startDrag(event, tableContainer),
    );
    tableContainer.addEventListener('mousemove', (event: MouseEvent) =>
      this.tableScrollService.onDrag(event, tableContainer),
    );
    tableContainer.addEventListener('mouseup', () =>
      this.tableScrollService.stopDrag(tableContainer),
    );
    tableContainer.addEventListener('mouseleave', () =>
      this.tableScrollService.stopDrag(tableContainer),
    );
    // Wheel event for horizontal scroll
    tableContainer.addEventListener('wheel', (event: WheelEvent) =>
      this.tableScrollService.handleHorizontalScroll(event, tableContainer),
    );
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.loadTable(this.currentPage);
  }

  loadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    const request$ = this.selectedReadOption == ReadsType.Raw
      ? this.client.getEnergyPerPhaseRegisters(this.requestData)
      : this.client.getProcessedEnergyPerPhaseRegisters(this.requestData);

    request$.subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count!;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
        if (response.count! == 0) {
          this.toastr.warning("Readings don't exist");
        }
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  toggleSelectOption(): void {
    this.isSelectOptionOpen = !this.isSelectOptionOpen;
  }

  closeSelectOption(): void {
    this.isSelectOptionOpen = false;
  }

  selectReadOption(option: ReadsType): void {
    this.selectedReadOption = option;
  }

  applyReadOption(): void {
    this.closeSelectOption();
    this.pageChange(1);
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    const data = new DownloadLoadProfileDataIn({
      meterId: this.requestData.meterId,
    });

    const request$ = this.selectedReadOption == ReadsType.Raw
      ? this.client.downloadEnergyPerPhaseFile(data)
      : this.client.downloadProcessedEnergyPerPhaseFile(data);

    request$.subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `EnergyPerPhase${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
